import React, { useState, useEffect, useCallback } from 'react'
import './App.css'
import Amplify from 'aws-amplify'
import { AmplifyAuthenticator, AmplifyConfirmSignUp, AmplifySignIn, AmplifySignUp } from '@aws-amplify/ui-react'
import { AuthState, onAuthUIStateChange } from '@aws-amplify/ui-components'
import { Auth } from '@aws-amplify/auth'
import awsconfig from './aws-exports'
import Button from 'react-bootstrap/Button'
import ClinicianPortal from './components/ClinicianPortal'
import { clearAuthTokenCache, getUserRoles, ROLES } from './util/authUtil'
import { Hub } from 'aws-amplify'
import { AuthProvider } from './context/AuthContext'
import { useIdleTimeout } from './hooks/useIdleTimeout'
import SessionTimeoutWarning from './components/SessionTimeoutWarning'
import { clearCognitoCookies } from './util/clearCognitoCookies'

/**
 * AuthStateApp - Main Application Entry Point
 * 
 * SECURITY FEATURES:
 * - No localStorage usage for tokens (HIPAA compliance)
 * - 15-minute idle timeout with warning
 * - Automatic logout on session expiry
 * - All tokens stored in memory only
 */

Amplify.configure(awsconfig)
Auth.configure(awsconfig)

/**
 * Main authenticated application content
 * Wrapped by AuthProvider for secure token management
 */
const AuthenticatedApp = ({ user, onLogout }) => {
  const [isUserHasPermission, setIsUserHasPermission] = useState(false)
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false)

  /**
   * Handle session timeout - log user out
   */
  const handleTimeout = useCallback(() => {
    console.log('Session timeout - logging out')
    setShowTimeoutWarning(false)
    onLogout()
  }, [onLogout])

  /**
   * Handle timeout warning - show modal
   */
  const handleWarning = useCallback(() => {
    console.log('Session timeout warning')
    setShowTimeoutWarning(true)
  }, [])

  /**
   * Initialize idle timeout hook: after 2 min idle show modal with 13 min countdown; logout at 15 min if no "Stay logged in"
   */
  const { stayLoggedIn, secondsRemaining } = useIdleTimeout({
    onWarning: handleWarning,
    onTimeout: handleTimeout,
    enabled: !!user,
    timeoutMs: 15 * 60 * 1000,       // 15 minutes total until logout
    warningDelayMs: 2 * 60 * 1000,   // show modal after 2 minutes idle
    warningMs: 13 * 60 * 1000         // 13 minute countdown in modal
  })

  /**
   * Handle "Stay Logged In" button click
   */
  const handleStayLoggedIn = useCallback(() => {
    setShowTimeoutWarning(false)
    stayLoggedIn()
  }, [stayLoggedIn])

  /**
   * Check if user has valid role permissions
   */
  const isUserHasRolePermission = (userGroups) => {
    if (!userGroups || userGroups.length === 0) {
      return false
    }
    return userGroups.some(role => ROLES.includes(role))
  }

  /**
   * Check user permissions on mount
   */
  useEffect(() => {
    if (user) {
      getUserRoles()
        .then(groups => {
          if (isUserHasRolePermission(groups)) {
            setIsUserHasPermission(true)
          }
        })
        .catch(err => {
          console.error('Error checking session:', err)
        })
    }
  }, [user])

  // User has permission - show main app
  if (isUserHasPermission && user) {
    return (
      <div className="App">
        <ClinicianPortal user={user} />
        <SessionTimeoutWarning
          isOpen={showTimeoutWarning}
          onStayLoggedIn={handleStayLoggedIn}
          onLogout={onLogout}
          secondsRemaining={secondsRemaining}
        />
      </div>
    )
  }

  // User signed in but no permission - show pending message
  return (
    <div className="App">
      <div>Welcome to team TBD. Your clinician account is being reviewed. When you are verified, you will receive an email notification.</div>
      <Button variant={'outline-info'} onClick={onLogout}>
        Logout
      </Button>
    </div>
  )
}

/**
 * Main AuthStateApp Component
 */
const AuthStateApp = () => {
  const [authState, setAuthState] = useState(null)
  const [user, setUser] = useState(null)

  /**
   * Secure logout handler
   * SECURITY: Clears all auth state and Cognito cookies
   */
  const logout = useCallback(async () => {
    try {
      await Auth.signOut()
    } catch (err) {
      console.error('Logout error:', err)
    }

    clearAuthTokenCache()
    // Clear any Cognito cookies that may exist
    clearCognitoCookies()
    
    // Reload page to ensure clean state
    window.location.reload()
  }, [])

  /**
   * Restore session on load (new tab / refresh) so we don't show login form briefly
   */
  useEffect(() => {
    let cancelled = false
    Auth.currentSession()
      .then((session) => {
        if (cancelled) return
        const idToken = session.getIdToken()
        const payload = idToken.payload
        setAuthState(AuthState.SignedIn)
        setUser({
          username: payload['cognito:username'] || payload.sub,
          attributes: {
            sub: payload.sub,
            email: payload.email,
            given_name: payload.given_name,
            family_name: payload.family_name,
          },
        })
      })
      .catch(() => {
        // No session - onAuthUIStateChange will set state when user signs in
      })
    return () => { cancelled = true }
  }, [])

  /**
   * Listen for auth state changes (sign in, sign out from UI)
   */
  useEffect(() => {
    return onAuthUIStateChange((nextAuthState, authData) => {
      setAuthState(nextAuthState)
      setUser(authData)
    })
  }, [])

  /**
   * Style overrides for auth pages
   */
  useEffect(() => {
    document.body.style.background = '#FDFAF1'
  }, [])

  /**
   * Listen for Hub auth events
   *
   * Profile creation intentionally does NOT happen on the `signUp` event:
   * at that point Cognito has not issued a session, so the POST would go out
   * without a Bearer token and be rejected as unauthenticated. Profile creation
   * is handled by `Navigation.initialize` on the first authenticated load,
   * where a 404 from `GET /secure/clinician/profile` triggers a recovery create.
   *
   * SECURITY: Removed localStorage.clear() calls
   */
  useEffect(() => {
    const hubListener = Hub.listen('auth', data => {
      switch (data.payload.event) {
        case 'signIn':
          console.log('User signed in')
          break
        case 'signOut':
          console.log('User signed out')
          // SECURITY: No localStorage.clear() - tokens are in memory only
          clearAuthTokenCache()
          setUser(null)
          setAuthState(null)
          break
        case 'signIn_failure':
          console.log('User sign in failed')
          break
        case 'configured':
          console.log('Auth module configured')
          break
        default:
          break
      }
    })

    return () => {
      hubListener()
    }
  }, [])

  // Render authenticated app
  if (authState === AuthState.SignedIn && user) {
    return (
      <AuthProvider>
        <AuthenticatedApp user={user} onLogout={logout} />
      </AuthProvider>
    )
  }

  // Render confirm signup
  if (authState === AuthState.ConfirmSignUp) {
    return (
      <AmplifyAuthenticator usernameAlias="email">
        <AmplifyConfirmSignUp slot="confirm-sign-up" usernameAlias="email" />
      </AmplifyAuthenticator>
    )
  }

  // Render login/signup
  return (
    <AmplifyAuthenticator usernameAlias="email">
      <AmplifySignUp
        slot={'sign-up'}
        usernameAlias="email"
        formFields={[
          {
            label: 'First Name',
            type: 'given_name',
            placeholder: 'Susan',
            required: true,
          },
          {
            label: 'Last Name',
            type: 'family_name',
            placeholder: 'Smith',
            required: true,
          },
          {
            type: 'email',
            placeholder: 'susan.smith@tbd.health',
            required: true,
          },
          {
            type: 'password',
            placeholder: '***************',
            required: true,
          },
          {
            type: 'phone_number',
            placeholder: '234-567-8901',
            required: true,
          },
        ]}
      />
      <AmplifySignIn slot="sign-in" usernameAlias="email" />
    </AmplifyAuthenticator>
  )
}

export default AuthStateApp
